import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ParametersNewTemplate } from "../models/ParametersNewTemplate";
import {
	createPMOrder,
	deleteTemplate,
	type UpsertTemplateResult,
	upsertTemplate,
} from "../repositories/TemplatesRepositorie";
import { TEMPLATES_QUERY_KEYS } from "./useTemplatesQueries";

export const useUpsertTemplate = ({
	onSuccess,
	onError,
}: {
	onSuccess?: (
		data: UpsertTemplateResult,
		variables: ParametersNewTemplate,
	) => void;
	onError?: (error: Error) => void;
} = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (template: ParametersNewTemplate) => upsertTemplate(template),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEYS.all });
			onSuccess?.(data, variables);
		},
		onError: (error: Error) => onError?.(error),
	});

	return { upsertTemplate: mutation.mutate, isSaving: mutation.isLoading };
};

export const useDeleteTemplate = ({
	onSuccess,
	onError,
}: {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
} = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (templateId: number) => deleteTemplate(templateId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});

	return { deleteTemplate: mutation.mutate, isDeleting: mutation.isLoading };
};

export const useCreatePMOrder = ({
	onSuccess,
	onError,
}: {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
} = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (templateId: number) => createPMOrder(templateId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});

	return { createPMOrder: mutation.mutate, isCreating: mutation.isLoading };
};
